import { Box, Typography } from "@mui/material";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import AirIcon from "@mui/icons-material/Air";
import NavigationIcon from "@mui/icons-material/Navigation";

import { UnitSystem } from "../../utils/units";
import {
  compassLabel,
  formatReading,
  formatWeatherTimestamp,
  WeatherPanelData,
  WeatherReading,
} from "./storyWeather";

/**
 * Readings panel for the story weather layer: the hour, then temperature,
 * humidity and wind as large figures with the across-the-map range when the
 * run had a spatial grid. "dark" floats beside the map (viewer/builder);
 * "light" sits inline under the print figure.
 */

const TEAL = "#00d4ad";

function Reading({
  icon,
  label,
  reading,
  kind,
  unitSystem,
  dark,
  extra,
}: {
  icon: React.ReactNode;
  label: string;
  reading: WeatherReading;
  kind: "temperature" | "humidity" | "wind";
  unitSystem: UnitSystem;
  dark: boolean;
  extra?: React.ReactNode;
}) {
  const f = formatReading(reading, kind, unitSystem);
  const text = dark ? "white" : "#1c2530";
  const muted = dark ? "rgba(255,255,255,0.65)" : "#5a6572";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      <Box sx={{ color: TEAL, display: "flex", flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{ color: muted, lineHeight: 1.2, display: "block" }}>
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "wrap" }}>
          <Typography
            component="span"
            sx={{ color: text, fontWeight: 700, fontSize: "1.35rem", lineHeight: 1.15 }}
          >
            {f.value}
          </Typography>
          <Typography component="span" variant="body2" sx={{ color: muted }}>
            {f.unit}
          </Typography>
          {extra}
        </Box>
        {f.range && (
          <Typography variant="caption" sx={{ color: muted, display: "block", lineHeight: 1.2 }}>
            {f.range}
            {f.unit} across the map
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface WeatherPanelCardProps {
  data: WeatherPanelData | null;
  unitSystem?: UnitSystem;
  variant?: "dark" | "light";
  /** Dark overlay only: which side of the map to float on. */
  side?: "left" | "right";
  /** Absolute-position inside the map container (dark only). */
  overlay?: boolean;
  /** Optional heading (e.g. the simulation name). */
  title?: string | null;
}

export default function WeatherPanelCard({
  data,
  unitSystem = "metric",
  variant = "dark",
  side = "right",
  overlay = true,
  title,
}: WeatherPanelCardProps) {
  if (!data) return null;
  const dark = variant === "dark";
  const text = dark ? "white" : "#1c2530";
  const muted = dark ? "rgba(255,255,255,0.65)" : "#5a6572";
  const from = compassLabel(data.windDirection);

  return (
    <Box
      className="story-weather-panel"
      sx={{
        ...(overlay && dark
          ? {
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              [side]: 16,
              zIndex: 3,
              pointerEvents: "none",
            }
          : {}),
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        px: 2,
        py: 1.5,
        width: dark ? 220 : "100%",
        maxWidth: dark ? 220 : 420,
        borderRadius: dark ? 2 : 1,
        bgcolor: dark ? "rgba(15, 23, 42, 0.9)" : "#f6f8fa",
        border: dark ? "none" : "1px solid #d5dbe2",
        boxShadow: dark ? "0 4px 16px rgba(0,0,0,0.4)" : "none",
        backdropFilter: dark ? "blur(4px)" : undefined,
      }}
    >
      <Box>
        <Typography variant="caption" sx={{ color: TEAL, fontWeight: 700, letterSpacing: 1.5 }}>
          {title?.trim() || "WEATHER"}
        </Typography>
        <Typography variant="body2" sx={{ color: text, fontWeight: 600, lineHeight: 1.3 }}>
          {formatWeatherTimestamp(data.timestamp)}
        </Typography>
      </Box>
      <Reading
        icon={<ThermostatIcon fontSize="small" />}
        label="Temperature"
        reading={data.temperature}
        kind="temperature"
        unitSystem={unitSystem}
        dark={dark}
      />
      <Reading
        icon={<WaterDropOutlinedIcon fontSize="small" />}
        label="Relative humidity"
        reading={data.humidity}
        kind="humidity"
        unitSystem={unitSystem}
        dark={dark}
      />
      <Reading
        icon={<AirIcon fontSize="small" />}
        label={from ? `Wind from ${from}` : "Wind"}
        reading={data.windSpeed}
        kind="wind"
        unitSystem={unitSystem}
        dark={dark}
        extra={
          data.windDirection != null ? (
            <NavigationIcon
              sx={{
                fontSize: 18,
                color: TEAL,
                alignSelf: "center",
                // The icon points up (north); wind blows TO direction + 180.
                transform: `rotate(${(data.windDirection + 180) % 360}deg)`,
              }}
            />
          ) : undefined
        }
      />
      {data.isGrid && (
        <Typography variant="caption" sx={{ color: muted, lineHeight: 1.2 }}>
          Centre values; ranges span the {data.pointCount}-point weather grid.
        </Typography>
      )}
    </Box>
  );
}
